import { cx } from "../cx";
import styles from "./DatePicker.module.css";
import {
  type DateRange,
  MONTH_NAMES,
  monthGrid,
  sameDay,
  startOfDay,
  WEEKDAY_LABELS_MONDAY,
  WEEKDAY_LABELS_SUNDAY,
  type WeekStart,
} from "./dates";

export interface MonthCalendarProps {
  month: Date;
  weekStart: WeekStart;
  today: Date;
  selected?: DateRange | null;
  /** Live preview while picking the second end of a range. */
  preview?: DateRange | null;
  onPick: (day: Date) => void;
  onHover?: (day: Date | null) => void;
  /** Adds prev/next arrows to the month title. */
  onPrev?: () => void;
  onNext?: () => void;
}

function within(d: Date, r: DateRange | null | undefined): boolean {
  if (!r) return false;
  const t = startOfDay(d).getTime();
  return t >= r.start.getTime() && t <= r.end.getTime();
}

export function MonthCalendar({
  month,
  weekStart,
  today,
  selected,
  preview,
  onPick,
  onHover,
  onPrev,
  onNext,
}: MonthCalendarProps) {
  const labels = weekStart === "MONDAY" ? WEEKDAY_LABELS_MONDAY : WEEKDAY_LABELS_SUNDAY;
  const range = preview ?? selected;
  const days = monthGrid(month, weekStart);
  return (
    <div className={styles.month}>
      <div className={styles.monthTitle}>
        {onPrev ? (
          <button type="button" className={styles.arrow} aria-label="Previous month" onClick={onPrev}>
            ‹
          </button>
        ) : (
          <span className={styles.arrowSpacer} />
        )}
        <span>
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </span>
        {onNext ? (
          <button type="button" className={styles.arrow} aria-label="Next month" onClick={onNext}>
            ›
          </button>
        ) : (
          <span className={styles.arrowSpacer} />
        )}
      </div>
      <div className={styles.grid}>
        {labels.map((l) => (
          <span key={l} className={styles.weekday} aria-hidden="true">
            {l}
          </span>
        ))}
        {days.map((d) => {
          const outside = d.getMonth() !== month.getMonth();
          const inRange = within(d, range);
          const isEdge = !!range && (sameDay(d, range.start) || sameDay(d, range.end));
          return (
            <button
              key={d.toISOString()}
              type="button"
              aria-pressed={inRange}
              aria-label={d.toDateString()}
              className={cx(
                styles.day,
                outside && styles.outside,
                inRange && styles.inRange,
                isEdge && styles.edge,
                sameDay(d, today) && styles.today,
              )}
              onClick={() => onPick(startOfDay(d))}
              onPointerEnter={onHover ? () => onHover(startOfDay(d)) : undefined}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
